import cn from 'classnames';
import React from 'react';

import dissolveSharedTextFieldOperation from 'editor/src/store/design/operation/dissolveSharedTextFieldOperation';
import updateSharedTextFieldOperation from 'editor/src/store/design/operation/updateSharedTextFieldOperation';
import getSharedTextFieldSummary, {
  SharedValueGroup,
  SharedValueMember,
} from 'editor/src/store/design/selector/getSharedTextFieldSummary';
import selectElementOperation from 'editor/src/store/editor/operation/selectElementOperation';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import spliceIntoTranslation, { TRANSLATION_SLOT } from 'editor/src/util/spliceIntoTranslation';
import useTr from 'editor/src/util/useTr';

import IconCross from 'editor/src/component/Icon/IconCross';
import IconLinked from 'editor/src/component/Icon/IconLinked';
import SharedTextFieldLabel from 'editor/src/component/SharedTextFieldLabel';

import styles from './index.module.scss';
import tabStyles from '../index.module.scss';

interface GroupProps {
  group: SharedValueGroup;
}

function SharedValueGroupCard({ group }: GroupProps) {
  const tr = useTr();
  const dispatch = useDispatch();

  // Not every member takes the value: one locked after it joined, or one
  // keeping a string per picklist option, stays in the group and prints, but
  // the buyer's typing never reaches it. Naming them plainly is the point of
  // listing the group at all — a summary that showed them alongside the rest
  // would say the value lands on pages where it does not.
  const idle = group.members.filter((member) => !member.receivesValue);

  return (
    <div className={cn(styles.group, 'cy-shared-values-group')}>
      <div className={styles.groupHeader}>
        {/* The same glyph the Layers tab puts on a linked layer, rather than a
            typographic arrow: one affordance, one drawing, and no dependence on
            which font the platform falls back to. */}
        <span className={styles.groupIcon} aria-hidden>
          <IconLinked />
        </span>
        <span className={cn(styles.value, { [styles.valueEmpty]: !group.value })}>
          {group.value || tr('editor-shared-values-no-value', 'No value yet')}
        </span>
        <button
          type="button"
          className={cn(styles.unlinkAll, 'cy-shared-values-unlink-all')}
          onClick={() => dispatch(dissolveSharedTextFieldOperation(group.sharedFieldId))}
        >
          {tr('editor-shared-values-unlink-all', 'Unlink all')}
        </button>
      </div>
      <div className={styles.members}>
        {group.members.map((member) => (
          <span
            key={member.uuid}
            className={cn(styles.chip, { [styles.chipIdle]: !member.receivesValue }, 'cy-shared-values-member')}
          >
            {/* Selecting also switches to that member's page, which is what
                makes the summary usable on a design whose group reaches a side
                the author is not looking at. */}
            {/* No aria-label: SharedTextFieldLabel renders plain text, so the
                accessible name comes from its contents — "Front - Name", the
                whole of what tells this chip from the one beside it. Spelling
                the layer name out alone would leave a two-sided card reading
                "Name" twice, which is the ambiguity that component exists to
                remove. The rule below cannot see through a component to the
                text it renders. */}
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <button
              type="button"
              className={styles.chipName}
              onClick={() => dispatch(selectElementOperation(member.uuid))}
              title={tr('editor-shared-values-show', 'Show this layer')}
            >
              <SharedTextFieldLabel name={member.name} spreadName={member.spreadName} />
            </button>
            <button
              type="button"
              className={cn(styles.chipRemove, 'cy-shared-values-remove')}
              onClick={() => dispatch(updateSharedTextFieldOperation(member.uuid, undefined))}
              aria-label={tr('editor-shared-values-remove', 'Stop this layer sharing')}
              title={tr('editor-shared-values-remove', 'Stop this layer sharing')}
            >
              <IconCross className={styles.chipRemoveIcon} />
            </button>
          </span>
        ))}
      </div>
      {idle.length > 0 && <IdleNote members={idle} />}
    </div>
  );
}

function IdleNote({ members }: { members: SharedValueMember[] }) {
  const tr = useTr();

  // Spliced in as labels rather than pre-joined into the sentence, so the names
  // here are written exactly as the chips above write them — same component.
  const names = members.map((member, index) => (
    <React.Fragment key={member.uuid}>
      {index > 0 && ', '}
      <SharedTextFieldLabel name={member.name} spreadName={member.spreadName} />
    </React.Fragment>
  ));

  // A translation that lost the placeholder falls back to the names alone:
  // which layers are excluded is the whole of what this note is for. See
  // spliceIntoTranslation.
  const fallback = 'The buyer’s value does not reach {{names}}: those layers keep their own.';

  return (
    <div className={cn(styles.note, 'cy-shared-values-idle-note')}>
      {spliceIntoTranslation(tr('editor-shared-values-not-filled', fallback, { names: TRANSLATION_SLOT }), names)}
    </div>
  );
}

/**
 * Every group of layers holding one value, in one place.
 *
 * A layer's own panel answers "what is THIS tied to", and only while that layer
 * is selected — so learning what a finished design shares means opening every
 * text field in turn. This is the other question, asked once: what is tied to
 * what, and where can a tie be undone.
 *
 * Read-and-undo rather than read-and-edit. Tying two layers together needs a
 * layer the author is not looking at yet, which is what the picker in the
 * field's own panel is for; the moves that belong here are the ones that need
 * the whole picture — drop one member, or break the group up.
 */
function SharedValuesSection() {
  const tr = useTr();
  const groups = useSelector(getSharedTextFieldSummary);

  // A heading over nothing is a heading in the way. The section is a statement
  // about the design, so it appears exactly when the design has something to
  // state — and it needs no host gate for the same reason: an imported template
  // sharing a value must be readable and undoable wherever it is opened, and a
  // host that cannot author simply never has a group to show.
  if (groups.length === 0) {
    return null;
  }

  return (
    <div className={cn(styles.SharedValuesSection, 'cy-shared-values-section')}>
      {/* The tab's own heading style, borrowed rather than copied: "Shared
          values" and "Personalized layers" are peers and have to look it. */}
      <div className={cn(tabStyles.personalizedLayerHeader, styles.title)}>
        {tr('editor-shared-values', 'Shared values')}
      </div>
      {groups.map((group) => (
        <SharedValueGroupCard key={group.sharedFieldId} group={group} />
      ))}
    </div>
  );
}

export default React.memo(SharedValuesSection);
